import { useCallback, useEffect, useState } from 'react';

import { toDocumentId, toFileId, useGetDocument, useGetFile, usePutFile } from '@/hooks/api';
import { JSONType } from '@/utils';

export type UseDocumentAnnotationsOpts = {
  documentId?: string | null;
};

export const useDocumentAnnotations = ({ documentId }: UseDocumentAnnotationsOpts) => {
  const { data: doc, isPending: isPendingDocument } = useGetDocument(toDocumentId(documentId));
  const { data: file, isPending: isPendingFile } = useGetFile(toFileId(doc?.annotationFileUrl));
  const { mutate: updateAnnotations } = usePutFile();
  const [annotations, setAnnotations] = useState<JSONType | undefined>();
  const [lastModified, setLastModified] = useState<Date | undefined>();

  useEffect(() => {
    if (!file) return;

    setAnnotations(undefined);
    setLastModified(undefined);

    file.text().then((annotationsStr) => {
      setAnnotations(JSON.parse(annotationsStr));
      setLastModified(new Date(file.lastModified));
    });
  }, [file]);

  const set = useCallback(
    (annotations: JSONType) => {
      return new Promise((resolve, reject) => {
        if (!doc) {
          reject(`${documentId} does not exist`);
          return;
        }
        updateAnnotations(
          {
            fileUrl: doc.annotationFileUrl,
            file: new Blob([JSON.stringify(annotations)], { type: 'application/json' }),
          },
          {
            onSuccess: (file) => resolve(file),
            onError: (error) => reject(error),
          }
        );
      });
    },
    [doc, documentId, updateAnnotations]
  );

  return { annotations, setAnnotations: set, isPending: isPendingDocument || isPendingFile, lastModified };
};
